/**
 * Workspace Health Card Component
 *
 * Displays health metrics and scores for the workspace:
 * - Resource utilization (agents being used vs available)
 * - User engagement (average sessions per user)
 * - Token efficiency (average tokens per session)
 */

import React from 'react';
import { Activity, TrendingUp, Gauge } from 'lucide-react';
import { WorkspaceAnalytics } from '../../../../../types/metrics';

interface WorkspaceHealthCardProps {
  analytics: WorkspaceAnalytics;
}

export const WorkspaceHealthCard: React.FC<WorkspaceHealthCardProps> = ({ analytics }) => {
  const {
    total_agents,
    agent_usage,
    total_sessions,
    total_active_users,
    total_members,
    token_usage,
  } = analytics;

  // Calculate health metrics
  const agentsUsed = agent_usage.length;
  const resourceUtilization = total_agents > 0 ? (agentsUsed / total_agents) * 100 : 0;

  const avgSessionsPerUser = total_active_users > 0 ? total_sessions / total_active_users : 0;

  const tokenEfficiency = total_sessions > 0 ? token_usage.tokens_used / total_sessions : 0;

  // Calculate member engagement rate
  const memberEngagement = total_members > 0 ? (total_active_users / total_members) * 100 : 0;

  const getHealthColor = (value: number, thresholds: { low: number; medium: number }) => {
    if (value >= thresholds.medium) return 'text-green-400';
    if (value >= thresholds.low) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getHealthBgColor = (value: number, thresholds: { low: number; medium: number }) => {
    if (value >= thresholds.medium) return 'bg-green-500/20';
    if (value >= thresholds.low) return 'bg-yellow-500/20';
    return 'bg-red-500/20';
  };

  const healthScores = [
    {
      label: 'Utilização de Recursos',
      value: resourceUtilization,
      unit: '%',
      icon: Gauge,
      thresholds: { low: 30, medium: 60 },
      description: `${agentsUsed} de ${total_agents} agentes em uso`,
    },
    {
      label: 'Engajamento de Membros',
      value: memberEngagement,
      unit: '%',
      icon: TrendingUp,
      thresholds: { low: 40, medium: 70 },
      description: `${total_active_users} de ${total_members} membros ativos`,
    },
    {
      label: 'Sessões por Usuário',
      value: avgSessionsPerUser,
      unit: '',
      icon: Activity,
      thresholds: { low: 3, medium: 10 },
      description: 'Média de sessões criadas',
    },
  ];

  return (
    <div className="bg-[#2a2a2a] rounded-xl p-6 border border-[#333333] h-[442px] overflow-auto">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white">Saúde do Workspace</h3>
        <p className="text-sm text-[#888888]">Indicadores de performance e engajamento</p>
      </div>

      <div className="space-y-4">
        {healthScores.map((score, index) => {
          const Icon = score.icon;
          const color = getHealthColor(score.value, score.thresholds);
          const bgColor = getHealthBgColor(score.value, score.thresholds);

          return (
            <div key={index} className="pb-4 border-b border-[#333333] last:border-b-0 last:pb-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${bgColor}`}>
                    <Icon className={`w-5 h-5 ${color}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{score.label}</p>
                    <p className="text-xs text-[#666666]">{score.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-bold ${color}`}>
                    {score.value.toFixed(1)}
                    {score.unit}
                  </p>
                </div>
              </div>
            </div>
          );
        })}

        {/* Token Efficiency */}
        <div className="pt-4 border-t border-[#333333]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white">Eficiência de Tokens</p>
              <p className="text-xs text-[#666666]">Média por sessão</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-[#F4CC04]">
                {tokenEfficiency >= 1000
                  ? `${(tokenEfficiency / 1000).toFixed(1)}K`
                  : tokenEfficiency.toFixed(0)}
              </p>
              <p className="text-xs text-[#666666]">tokens/sessão</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
